export const GUIDE_CONTENT = {
  title: "Roles & Permissions",
  overview:
    "This guide explains how roles and permissions work in the AllWell platform, what each role can do, how permissions are organized, and how to configure access for your team.",
  userRolesIntro:
    "Access levels on the platform are controlled by roles assigned to each contracted client or entity. The SmartMonitor Administrator initially sets up these roles during the client onboarding process. However, the client or entity retains the ability to customize permissions as needed to meet their organization's specific requirements.",
  scopeTip:
    "Scope defines the boundary within which a role operates. System-wide roles can access everything. Entity-level roles are limited to their entity and its clients. Client-level roles are limited to a single client. Patient-level roles can only access assigned patients.",
};

export const ROLE_GUIDE_DATA = [
  {
    id: "system-admin",
    title: "System Admin",
    scope: "System-Wide",
    description:
      "The Super Admin role is strictly reserved for platform administrators at SmartMonitor. This role has unrestricted, high-level access across all clients and entities. It is essential for system-level management, including overseeing the permission structure, managing admin user accounts, onboarding device users, and handling orders and billing.",
    canDo: [
      "Full access across all entities and their clients",
      "Manage system-level configurations",
      "Modify any role",
      "Access all dashboards and reports",
      "Override any permission assignment",
    ],
    cannotDo: ["No restrictions applied to the role"],
  },
  {
    id: "system-user",
    title: "System User",
    scope: "System-Wide",
    description:
      "The System User role is exclusively designated for users within the SmartMonitor platform. This role has high-level, restricted access across all clients and entities. It is intended for a group that includes non-admin personnel, such as developers and quality assurance teams, as well as AllWell Developers. These users require access to the portal to view device user accounts and troubleshoot any production issues.",
    canDo: [
      "Limited access across all entities and their clients",
      "View system-level configurations",
      "Access all dashboards and reports",
    ],
    cannotDo: [
      "Read-only access to device user accounts across entities and other clients",
    ],
  },
  {
    id: "administrator",
    title: "Administrator",
    scope: "Client-level, Entity-level",
    description:
      "The Admin has full control within a contracted and assigned Entity or Client. They can manage admin users, device users, configure device settings, assign roles, and oversee all operational permissions within their scope.",
    canDo: [
      "Manage admin and device users within their client/entity",
      "Assign and remove roles",
      "Configure and manage device settings",
      "View all permission assignments",
      "Onboard new device users",
    ],
    cannotDo: [
      "Access other entities or clients",
      "Modify system-level configurations",
    ],
  },
  {
    id: "manager",
    title: "Manager",
    scope: "Client-level",
    description:
      "The Client Manager role has restricted access limited to their assigned Client. Permissions are set by the Client Admin. Responsibilities include adding admin and device users, managing contacts, and device settings. This role does not have access to billing or administrative functions.",
    canDo: [
      "Manage admin users within their client/entity",
      "Configure device settings of customers",
      "Onboard new device users",
    ],
    cannotDo: [
      "Access other entities or clients",
      "Modify system-level configurations",
      "Manage billing and payments",
    ],
  },
  {
    id: "case-manager",
    title: "Case Manager",
    scope: "Device User-level",
    description:
      "Case Managers, also known as Care Representatives (CREs), are responsible for daily patient case management. Their duties include updating the profiles of device users, managing contacts, and setting up customer devices. They do not have access to billing or administrative functions. The administrator can customize a Case Manager's specific privileges based on organizational needs.",
    canDo: [
      "View device user profiles and their device settings",
      "View alerts and geo location",
    ],
    cannotDo: [
      "Manage billing or orders",
      "Assign roles or permissions",
      "Deactivate the device user's profile",
    ],
  },
];

export const CASE_MANAGER_CUSTOMIZATION_DATA = [
  "Smart-Monitor Administrator - at the Entity Level and Client Level.",
  "Entity Administrator - for their own permissions at the Entity Level and for a specific client.",
  "Client Administrator - for their own permissions at the Client Level.",
];

export const PERMISSION_CATEGORIES = [
  {
    title: "Admin User Management",
    description: "Add users and manage their portal access and permissions.",
  },
  {
    title: "Client Management",
    description: "Manage client-level details and configurations.",
  },
  {
    title: "Entity Management",
    description: "Manage entity-level details and configurations.",
  },
  {
    title: "Device User Management",
    description:
      "Manage device user onboarding, contacts, device settings, deactivation, SIM assignment verification, geo-location, and alert management.",
  },
  {
    title: "Reports Management",
    description: "Access to reports available in the AllWell Portal.",
  },
  {
    title: "Billing & Subscription",
    description:
      "Manage orders, device user subscription, payment methods, and transaction (invoice) records.",
  },
  {
    title: "Device Management",
    description:
      "Manage the device pool by tracking assigned devices and devices available for assignment.",
  },
];

export const ACTION_TYPES = [
  {
    action: "Create",
    meaning: "Add a new record",
    example: "Client: Create - allows the role to create a new client.",
  },
  {
    action: "View",
    meaning: "Read-only access to data",
    example:
      "Device Setting: View - see the device user's device setting without editing.",
  },
  {
    action: "Update",
    meaning: "Modify existing records",
    example: "Device User Profile: Update - edit a device user's profile details.",
  },
  {
    action: "Delete",
    meaning: "Soft-delete the record from the database",
    example: "Contacts: Delete - removes selected contacts from the list.",
  },
  {
    action: "Deactivate",
    meaning: "Disable the record without permanent deletion",
    example: "Admin User: Deactivate - suspend a user's account.",
  },
  {
    action: "Onboard",
    meaning: "Onboard a new user",
    example: "Device User: Onboard - onboard a new device user onto the portal.",
  },
  {
    action: "Export",
    meaning: "Access to export data from the portal",
    example: "Reports: Export - download report data as a file.",
  },
  {
    action: "Manage Payment For Client",
    meaning: "Access to payment method",
    example:
      "Payment Methods: Manage Payment for Client - allows admin to manage payments for the client and their device users.",
  },
];

export const CONFIGURE_PERMISSION_STEPS = [
  {
    title: "Select your context",
    description:
      "Choose whether you're customizing permissions for a Client or an Entity using the toggle at the top of the page. Then select the specific Entity and Client from the dropdowns.",
    bullets: [
        "Platform Level - Permissions are applied at the system level.",
      "Entity Level - when an entity is selected, permissions are applied to roles associated with the selected entity and all of its clients.",
      "Client Level - when a client is selected, customized permissions are applied to the roles of that specific client only.",
    ],
  },
  {
    title: "Select a role",
    description:
      "Select the role you want to configure - Admin, Case Manager/CRE, or SysAdmin. Each role has a distinct set of default permissions tailored to its responsibilities.",
  },
  {
    title: "Review permission categories",
    description:
      "Permissions are grouped into categories like Patient Management, Order & Billing, and Reports. Expand any category to see sub-groups and individual permissions.",
  },
  {
    title: "Enable or Disable permissions",
    description:
      "Click individual action pills (Create, View, Update, etc.) to enable or disable specific permissions. Use Select all to bulk-enable an entire category or sub-group.",
  },
  {
    title: "Review & save",
    description:
      "The footer shows a live count of assigned permissions and a progress bar. When you're satisfied, click Save Changes. To discard, click Cancel.",
  },
];

export const IMPORTANT_NOTES = [
  "When a role is customized at the Entity Level, users of that entity and all clients under it follow the customized permissions.",
  "When a role is customized at the Client Level, only users of that specific client follow the customized permissions.",
  "Default permissions for each role are set by the AllWell System Administrator during initial onboarding. Customizations override defaults at the selected scope.",
  "Permission changes take effect immediately; notify affected users in advance of significant changes.",
];
